import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { AuthContext } from '../App';
import { BookOpen, Brain, Puzzle, Trophy, Star, Mail, User, Lock, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import './Landing.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Landing() {
  const { t } = useTranslation();
  const { user, setUser } = React.useContext(AuthContext);
  const navigate = useNavigate();
  
  const [authMode, setAuthMode] = useState(null); // null, 'login', 'register'
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Form states
  const [registerForm, setRegisterForm] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: ''
  });
  
  const [loginForm, setLoginForm] = useState({
    emailOrUsername: '',
    password: ''
  });
  
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);
  
  const handleGoogleLogin = () => {
    const redirectUrl = `${window.location.origin}/dashboard`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  
  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!registerForm.email || !registerForm.username || !registerForm.password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (registerForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (registerForm.username.length < 3) {
      toast.error('Username must be at least 3 characters');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, {
        email: registerForm.email,
        username: registerForm.username,
        password: registerForm.password
      }, { withCredentials: true });
      
      toast.success('Account created successfully!');
      setUser(response.data.user);
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    
    if (!loginForm.emailOrUsername || !loginForm.password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login-email`, {
        email_or_username: loginForm.emailOrUsername,
        password: loginForm.password
      }, { withCredentials: true });
      
      toast.success('Login successful!');
      setUser(response.data.user);
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };
  
  const features = [
    { icon: <BookOpen size={48} />, title: t('quizzes'), color: '#FF6B9D' },
    { icon: <Puzzle size={48} />, title: 'Learning Wheel', color: '#A8E6CF' },
    { icon: <Trophy size={48} />, title: t('leaderboard'), color: '#FFE66D' },
    { icon: '✈️', title: 'Airplane Quiz Game', color: '#4A90E2', isEmoji: true },
    { icon: '🧀', title: 'Cheese Chase', color: '#f59e0b', isEmoji: true }
  ];
  
  return (
    <div className="landing-page">
      <motion.div 
        className="hero-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="hero-content">
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="hero-logo-container"
          >
            <img src="/al3abi-logo.png" alt="Al3abi" className="hero-logo" />
          </motion.div>
          
          <h1 className="hero-title" data-testid="landing-title">
            {t('welcome')}
          </h1>
          
          <p className="hero-subtitle">
            Learn, Play, and Grow with Fun Educational Games!
          </p>
          
          <AnimatePresence mode="wait">
            {!authMode ? (
              <motion.div
                key="buttons"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="auth-buttons"
              >
                <button 
                  onClick={handleGoogleLogin} 
                  className="btn btn-primary btn-large"
                  data-testid="google-login-button"
                >
                  <Star size={24} />
                  {t('login')}
                </button>
                
                <div className="auth-divider">
                  <span>or</span>
                </div>
                
                <button 
                  onClick={() => setAuthMode('register')} 
                  className="btn btn-secondary btn-large"
                  data-testid="create-account-button"
                >
                  <User size={24} />
                  Create an Account
                </button>
                
                <p className="login-link">
                  Already have an account? {' '}
                  <button onClick={() => setAuthMode('login')} className="link-btn">
                    Login with Email
                  </button>
                </p>
              </motion.div>
            ) : authMode === 'register' ? (
              <motion.div
                key="register"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="auth-form-container"
              >
                <button className="back-btn" onClick={() => setAuthMode(null)}>
                  <ArrowLeft size={20} /> Back
                </button>
                
                <h2 className="auth-form-title">Create Account</h2>
                
                <form onSubmit={handleRegister} className="auth-form">
                  <div className="form-group">
                    <Label htmlFor="email">Email</Label>
                    <div className="input-with-icon">
                      <Mail size={18} className="input-icon" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="Enter your email"
                        value={registerForm.email}
                        onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})}
                        data-testid="register-email"
                      />
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <Label htmlFor="username">Username</Label>
                    <div className="input-with-icon">
                      <User size={18} className="input-icon" />
                      <Input
                        id="username"
                        type="text"
                        placeholder="Choose a username"
                        value={registerForm.username}
                        onChange={(e) => setRegisterForm({...registerForm, username: e.target.value})}
                        data-testid="register-username"
                      />
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <Label htmlFor="password">Password</Label>
                    <div className="input-with-icon">
                      <Lock size={18} className="input-icon" />
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Create a password"
                        value={registerForm.password}
                        onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})}
                        data-testid="register-password"
                      />
                      <button 
                        type="button" 
                        className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <div className="input-with-icon">
                      <Lock size={18} className="input-icon" />
                      <Input
                        id="confirmPassword"
                        type={showPassword ? "text" : "password"}
                        placeholder="Confirm your password"
                        value={registerForm.confirmPassword}
                        onChange={(e) => setRegisterForm({...registerForm, confirmPassword: e.target.value})}
                        data-testid="register-confirm-password"
                      />
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="submit-btn"
                    disabled={loading}
                    data-testid="register-submit"
                  >
                    {loading ? 'Creating Account...' : 'Create Account'}
                  </Button>
                </form>
                
                <p className="switch-auth">
                  Already have an account? {' '}
                  <button onClick={() => setAuthMode('login')} className="link-btn">
                    Login
                  </button>
                </p>
              </motion.div>
            ) : (
              <motion.div
                key="login"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="auth-form-container"
              >
                <button className="back-btn" onClick={() => setAuthMode(null)}>
                  <ArrowLeft size={20} /> Back
                </button>
                
                <h2 className="auth-form-title">Login</h2>
                
                <form onSubmit={handleEmailLogin} className="auth-form">
                  <div className="form-group">
                    <Label htmlFor="emailOrUsername">Email or Username</Label>
                    <div className="input-with-icon">
                      <Mail size={18} className="input-icon" />
                      <Input
                        id="emailOrUsername"
                        type="text"
                        placeholder="Enter email or username"
                        value={loginForm.emailOrUsername}
                        onChange={(e) => setLoginForm({...loginForm, emailOrUsername: e.target.value})}
                        data-testid="login-email"
                      />
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <Label htmlFor="loginPassword">Password</Label>
                    <div className="input-with-icon">
                      <Lock size={18} className="input-icon" />
                      <Input
                        id="loginPassword"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={loginForm.password}
                        onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                        data-testid="login-password"
                      />
                      <button 
                        type="button" 
                        className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="submit-btn"
                    disabled={loading}
                    data-testid="login-submit"
                  >
                    {loading ? 'Logging in...' : 'Login'}
                  </Button>
                </form>
                
                <div className="auth-divider">
                  <span>or</span>
                </div>
                
                <button 
                  onClick={handleGoogleLogin} 
                  className="btn btn-google"
                  data-testid="google-login-alt"
                >
                  <Star size={20} />
                  Login with Google
                </button>
                
                <p className="switch-auth">
                  Don't have an account? {' '}
                  <button onClick={() => setAuthMode('register')} className="link-btn">
                    Create Account
                  </button>
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
      
      <div className="features-section">
        <h2 className="features-title">Explore Our Games</h2>
        <div className="features-grid">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className="feature-card"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              whileHover={{ scale: 1.05, y: -10 }}
              style={{ borderTop: `4px solid ${feature.color}` }}
            >
              <div className="feature-icon" style={{ color: feature.color }}>
                {feature.isEmoji ? <span style={{ fontSize: '48px' }}>{feature.icon}</span> : feature.icon}
              </div>
              <h3 className="feature-title">{feature.title}</h3>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Landing;
